/*
  The full-screen "your lesson is starting" alarm while the app is open (website, installed web
  app, or the phone app in the foreground). When the phone app is closed the Android/iPhone alarm
  takes over (see native/). Rings a loud repeating tone with Web Audio, vibrates where allowed.
*/
import { BellFilled } from "@ant-design/icons";
import { Button } from "app/components/v1/Button";
import { alarmSlice, MAIN_SELECTORS } from "app/store";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

class Ringer {
  private ctx: AudioContext | null = null;
  private timer: number | null = null;
  private vib: number | null = null;
  start(sound: boolean, vibrate: boolean) {
    this.stop();
    if (sound) {
      try {
        this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const beep = () => {
          if (!this.ctx) return;
          const t = this.ctx.currentTime;
          [0, 0.18, 0.36].forEach((offset, i) => {
            const osc = this.ctx!.createOscillator(); const gain = this.ctx!.createGain();
            osc.type = "square"; osc.frequency.value = i === 1 ? 1320 : 1760;
            gain.gain.setValueAtTime(0.0001, t + offset); gain.gain.exponentialRampToValueAtTime(0.5, t + offset + 0.01); gain.gain.exponentialRampToValueAtTime(0.0001, t + offset + 0.15);
            osc.connect(gain).connect(this.ctx!.destination); osc.start(t + offset); osc.stop(t + offset + 0.16);
          });
        };
        beep();
        this.timer = window.setInterval(beep, 1000);
      } catch { /* no audio on this device */ }
    }
    if (vibrate && "vibrate" in navigator) {
      const buzz = () => navigator.vibrate?.([600, 300, 600]);
      buzz(); this.vib = window.setInterval(buzz, 1800);
    }
  }
  stop() {
    if (this.timer) clearInterval(this.timer); this.timer = null;
    if (this.vib) clearInterval(this.vib); this.vib = null;
    navigator.vibrate?.(0);
    this.ctx?.close().catch(() => undefined); this.ctx = null;
  }
}
const ringer = new Ringer();
const RING_MS = 60_000;

export default function AlarmOverlay() {
  const ringing = useSelector(MAIN_SELECTORS.alarm);
  const prefs = useSelector(MAIN_SELECTORS.reminders);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [now, setNow] = useState(moment());
  const timeout = useRef<number | null>(null);

  useEffect(() => {
    if (!ringing) { ringer.stop(); return; }
    ringer.start(prefs.sound, prefs.vibrate);
    const tick = setInterval(() => setNow(moment()), 1000);
    timeout.current = window.setTimeout(() => dispatch(alarmSlice.actions.stop()), RING_MS);
    return () => { clearInterval(tick); if (timeout.current) clearTimeout(timeout.current); ringer.stop(); };
  }, [ringing, prefs.sound, prefs.vibrate, dispatch]);

  if (!ringing) return null;
  const dismiss = () => dispatch(alarmSlice.actions.stop());
  return (
    <div className="ct-alarm-overlay" role="alertdialog" aria-live="assertive">
      <div className="icon"><BellFilled style={{ fontSize: 54 }} /></div>
      <div className="time">{now.format("h:mm")}</div>
      <h1>{ringing.title}</h1>
      <p>{ringing.body}</p>
      <div className="mt-4 w-100" style={{ maxWidth: 360 }}>
        <Button className="w-100" size="lg" buttonType="white" name="Open lesson" onClick={() => { dismiss(); navigate(`/lesson/${ringing.registerId}`); }} />
        <Button className="w-100 mt-2" size="lg" buttonType="text" color="primary" style={{ color: "#fff" }} name="Dismiss" onClick={dismiss} />
      </div>
    </div>
  );
}
